import { z } from 'zod';

/**
 * Parceiros e patrocinadores (decidido pelo GrowuP a 19-10-2026): uma so lista
 * que alimenta a pagina `growup.gg/parceiros` e o carrossel junto ao rodape.
 * Gerem-se em Conteudos > Parceiros (`partners.manage`: Gestor de Conteudo e
 * Administrador geral). Ver docs/conteudos.
 *
 * Prefixos HTTP:
 *   - `/api/parceiros`                     site publico (publicados e dentro das datas);
 *   - `/api/site/content/parceiros/...`    back-office, so em gestao.
 */

/** Uma imagem carregada (`/api/media/...`); nada de imagens de terceiros. */
const mediaSrcSchema = z
  .string()
  .trim()
  .regex(/^\/api\/media\/[0-9a-f-]{36}\/(sm|md|lg)\.webp$/, 'carregue o logótipo no back-office');

/** Os grupos da pagina, por esta ordem. */
export const PARTNER_GROUPS = [
  'institutional',
  'main_sponsor',
  'sponsor',
  'partner',
  'media',
] as const;
export const partnerGroupSchema = z.enum(PARTNER_GROUPS);
export type PartnerGroup = z.infer<typeof partnerGroupSchema>;

export const PARTNER_GROUP_LABELS: Record<PartnerGroup, string> = {
  institutional: 'Parceiros institucionais',
  main_sponsor: 'Patrocinador principal',
  sponsor: 'Patrocinadores',
  partner: 'Parceiros',
  media: 'Media partners',
};

/** `draft`: so no back-office; `published`: no site. */
export const partnerStatusSchema = z.enum(['draft', 'published']);
export type PartnerStatus = z.infer<typeof partnerStatusSchema>;

const dateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .nullable()
  .default(null);

/** Um parceiro na pagina `/parceiros` (e, mais tarde, no carrossel). */
export const publicPartnerSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  group: partnerGroupSchema,
  logoUrl: z.string(),
  /** Vazio = sem ligacao. */
  websiteUrl: z.string(),
  description: z.string(),
  sinceYear: z.number().int().nullable(),
  inCarousel: z.boolean(),
});
export type PublicPartner = z.infer<typeof publicPartnerSchema>;

/** Os parceiros agrupados, pela ordem de `comparePartners`. */
export function partnersByGroup<T extends { group: PartnerGroup }>(
  partners: readonly T[],
): { group: PartnerGroup; partners: T[] }[] {
  const groups: { group: PartnerGroup; partners: T[] }[] = [];
  for (const partner of partners) {
    const last = groups.at(-1);
    if (last?.group === partner.group) last.partners.push(partner);
    else groups.push({ group: partner.group, partners: [partner] });
  }
  return groups;
}

export const partnerInputSchema = z
  .object({
    name: z.string().trim().min(1, 'escreva o nome do parceiro').max(120),
    group: partnerGroupSchema,
    logoUrl: mediaSrcSchema.nullable().default(null),
    websiteUrl: z
      .string()
      .trim()
      .max(500)
      .refine((v) => v === '' || /^https:\/\/[^\s]+$/.test(v), {
        message: 'use um link https://',
      })
      .default(''),
    description: z.string().trim().max(400).default(''),
    sinceYear: z.number().int().min(1990).max(2200).nullable().default(null),
    inCarousel: z.boolean().default(true),
    startsOn: dateSchema,
    endsOn: dateSchema,
  })
  .refine((p) => p.startsOn === null || p.endsOn === null || p.startsOn <= p.endsOn, {
    message: 'o fim tem de ser depois do início',
    path: ['endsOn'],
  });
export type PartnerInput = z.input<typeof partnerInputSchema>;

export const adminPartnerSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  group: partnerGroupSchema,
  logoUrl: z.string().nullable(),
  websiteUrl: z.string(),
  description: z.string(),
  sinceYear: z.number().int().nullable(),
  inCarousel: z.boolean(),
  startsOn: z.string().nullable(),
  endsOn: z.string().nullable(),
  status: partnerStatusSchema,
  sortOrder: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Login de quem criou e de quem mexeu por ultimo. */
  createdBy: z.string(),
  updatedBy: z.string(),
});
export type AdminPartner = z.infer<typeof adminPartnerSchema>;

export const partnerOrderRequestSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(1000),
});

/** A ordem da pagina e do back-office: o grupo, depois a ordem a mao. */
export function comparePartners(
  a: { group: PartnerGroup; sortOrder: number },
  b: { group: PartnerGroup; sortOrder: number },
): number {
  return (
    PARTNER_GROUPS.indexOf(a.group) - PARTNER_GROUPS.indexOf(b.group) || a.sortOrder - b.sortOrder
  );
}

/**
 * Se o parceiro esta dentro das datas no dia `today` (`aaaa-mm-dd`). Fora
 * delas sai sozinho do site, mesmo publicado.
 */
export function partnerActiveOn(
  p: { startsOn: string | null; endsOn: string | null },
  today: string,
): boolean {
  return (p.startsOn === null || p.startsOn <= today) && (p.endsOn === null || today <= p.endsOn);
}
